const HOUSE_ALIASES = {
  adp: 'acqua di parma',
  armani: 'giorgio armani',
  ch: 'carolina herrera',
  dg: 'dolce gabbana',
  jpg: 'jean paul gaultier',
  mfk: 'maison francis kurkdjian',
  pdm: 'parfums de marly',
  replica: 'maison margiela',
  tf: 'tom ford',
  ysl: 'yves saint laurent',
}

const CONCENTRATION_ALIASES = {
  cologne: 'edc',
  eau_de_cologne: 'edc',
  eau_de_parfum: 'edp',
  eau_de_toilette: 'edt',
  extrait_de_parfum: 'extrait',
  le_parfum: 'parfum',
}

export const SEARCH_MISS_CONFIDENCE_THRESHOLD = 0.9
// Dual soft-hit gate, shared with fragrance.search_catalog_findability_v1
// (FOS-SEARCH-SOFT-HIT-002). Empty the list only when the best identity
// score is below the FE miss threshold AND the best trigram rank is below
// this floor. 0.35 leaked `asdfghjkl perfume` (~0.39) and
// `xyzzy fragrance os test` (~0.41). 0.45 sits above those dumps and
// still keeps creed (rank ~0.43, conf 0.99) and hom/sav prefixes
// (conf≥0.95). Rank-only would hide prefixes. A confidence-only gate
// used to hide nine pm (conf 0.44 before FOS-SEARCH-ALIAS-CONF-001,
// rank 1.0); alias identity now scores ≥0.9 via digit↔letter split.
// Muse/Aura full-set 0.89 cap is unchanged.
export const SOFT_HIT_RANK_FLOOR = 0.45

const clamp = value => Math.max(0, Math.min(1, Number(value) || 0))

export function normalizeCatalogSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function expandTokens(value) {
  const normalized = normalizeCatalogSearchText(value)
  const wholeAlias = HOUSE_ALIASES[normalized.replace(/\s+/g, '_')]
    || CONCENTRATION_ALIASES[normalized.replace(/\s+/g, '_')]
  if (wholeAlias) return wholeAlias.split(' ')

  return normalized.split(' ').filter(Boolean).flatMap(token => {
    const expanded = HOUSE_ALIASES[token] || CONCENTRATION_ALIASES[token]
    return (expanded ? expanded.split(' ') : [token]).flatMap(part =>
      part.replace(/([0-9])([a-z])/g, '$1 $2')
        .replace(/([a-z])([0-9])/g, '$1 $2')
        .split(' ')
        .filter(Boolean))
  })
}

function editDistance(left, right) {
  if (left === right) return 0
  if (!left.length) return right.length
  if (!right.length) return left.length
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  for (let i = 1; i <= left.length; i += 1) {
    const current = [i]
    for (let j = 1; j <= right.length; j += 1) {
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1),
      )
    }
    previous = current
  }
  return previous[right.length]
}

function tokenScore(queryToken, candidateToken) {
  if (queryToken === candidateToken) return 1
  // A user who types "bacca" has supplied a strong partial, not an ambiguous
  // substring. Require three characters and a word start to avoid noisy hits.
  if (queryToken.length >= 3 && candidateToken.startsWith(queryToken)) return 0.96
  // Adjacent transpositions have Levenshtein distance two ("frod"/"ford").
  // This remains safe here because every query token must independently match
  // the same candidate identity before the result can clear 0.90.
  const allowedEdits = Math.min(queryToken.length, candidateToken.length) >= 4 ? 2 : 1
  if (Math.min(queryToken.length, candidateToken.length) >= 4
      && Math.abs(queryToken.length - candidateToken.length) <= allowedEdits
      && editDistance(queryToken, candidateToken) <= allowedEdits) return 0.93
  return 0
}

function rowTokens(row) {
  return expandTokens([
    row?.house,
    row?.name,
    row?.concentration_display,
    row?.concentration_normalized,
    row?.concentration_raw_text,
    row?.concentration,
    ...(row?.concentration_alternate_values || []),
  ].filter(Boolean).join(' '))
}

export function catalogMatchConfidence(query, row) {
  if (!row) return { confidence: 0, reason: 'no_result' }
  const serverConfidence = Number(row.match_confidence)
  if (Number.isFinite(serverConfidence)) {
    return {
      confidence: clamp(serverConfidence),
      reason: row.match_reason || 'server_scored',
    }
  }

  const queryTokens = expandTokens(query)
  const candidates = rowTokens(row)
  if (!queryTokens.length || !candidates.length) return { confidence: 0, reason: 'unscorable' }

  const tokenScores = queryTokens.map(token =>
    candidates.reduce((best, candidate) => Math.max(best, tokenScore(token, candidate)), 0))
  if (tokenScores.some(score => score === 0)) {
    return {
      confidence: clamp(tokenScores.reduce((sum, score) => sum + score, 0) / queryTokens.length * 0.88),
      reason: 'partial_token_coverage',
    }
  }

  const weakest = Math.min(...tokenScores)
  if (weakest >= 1) return { confidence: 0.99, reason: 'all_tokens_exact' }
  if (weakest >= 0.96) return { confidence: 0.95, reason: 'all_tokens_prefix' }
  return { confidence: 0.91, reason: 'all_tokens_typo_tolerant' }
}

export function applySoftHitPolicy(query, rows = [], threshold = SEARCH_MISS_CONFIDENCE_THRESHOLD) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || []
  let bestConf = 0
  let bestRank = 0
  for (const row of rows) {
    const { confidence } = catalogMatchConfidence(query, row)
    if (confidence > bestConf) bestConf = confidence
    const rank = Number(row?.rank)
    if (Number.isFinite(rank) && rank > bestRank) bestRank = rank
  }
  if (bestConf < threshold && bestRank < SOFT_HIT_RANK_FLOOR) return []
  return rows
}

export function catalogSearchDecision(query, rows = [], threshold = SEARCH_MISS_CONFIDENCE_THRESHOLD) {
  let best = { confidence: 0, reason: rows.length ? 'low_confidence' : 'zero_results', row: null }
  for (const row of rows) {
    const scored = catalogMatchConfidence(query, row)
    if (scored.confidence > best.confidence) best = { ...scored, row }
  }
  return {
    ...best,
    shouldDiscover: rows.length === 0 || best.confidence < threshold,
    fallbackReason: rows.length === 0 ? 'zero_results' : best.confidence < threshold ? 'low_confidence' : null,
  }
}
